import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Spinner } from '@repo/design-system-kit';
import { useFetchClientPortal } from 'receivable-invoices-js/react/api/queries/fetch-client-portal';
import { useFetchQuotes } from 'receivable-invoices-js/react/api/queries/fetch-quotes';
import { useFetchInvoices } from 'receivable-invoices-js/react/api/queries/fetch-invoices';
import { useFetchClientPortalSettings } from 'receivable-invoices-js/react/api/queries/fetch-client-portal-settings';
import { useLocaleManager } from 'receivable-invoices-js/react/hooks/use-locale-manager';
import { ClientPortal } from '../../components/client-portal/client-portal';

export function ClientPortalRoute(): React.ReactNode {
  const { id = '' } = useParams<{ id: string }>();
  const localeManager = useLocaleManager();
  const navigate = useNavigate();

  const {
    data: clientPortalSettingsData,
    error: clientPortalSettingsError,
    isLoading: isClientPortalSettingsLoading,
  } = useFetchClientPortalSettings({ clientId: id });

  const {
    data: clientPortalData,
    error: clientPortalError,
    isLoading: isClientPortalLoading,
  } = useFetchClientPortal(
    { clientId: id },
    {},
    { enabled: Boolean(clientPortalSettingsData?.clientPortalEnabled) }
  );

  const {
    data: quotesData,
    error: quotesError,
    isLoading: isQuotesLoading,
  } = useFetchQuotes(
    { clientId: id },
    {},
    { enabled: Boolean(clientPortalSettingsData?.clientPortalEnabled) }
  );

  const {
    data: invoicesData,
    error: invoicesError,
    isLoading: isInvoicesLoading,
  } = useFetchInvoices(
    { clientId: id },
    {},
    { enabled: Boolean(clientPortalSettingsData?.clientPortalEnabled) }
  );

  useEffect(() => {
    if (
      clientPortalSettingsError ||
      (clientPortalSettingsData && !clientPortalSettingsData.clientPortalEnabled)
    ) {
      void navigate('/error');
    }
  }, [clientPortalSettingsData, clientPortalSettingsError, navigate]);

  useEffect(() => {
    if (!isQuotesLoading && !isInvoicesLoading) {
      if (quotesData?.length) {
        const locale = quotesData[0]?.locale;
        localeManager.setLocale(locale ?? 'en');
      } else if (invoicesData?.length) {
        const locale = invoicesData[0]?.locale;
        localeManager.setLocale(locale ?? 'en');
      }
    }
  }, [isInvoicesLoading, isQuotesLoading, quotesData, invoicesData, localeManager]);

  if (
    isClientPortalSettingsLoading ||
    isClientPortalLoading ||
    isQuotesLoading ||
    isInvoicesLoading
  ) {
    return (
      <div
        data-test-loading
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Spinner size="large" />
      </div>
    );
  }

  if (clientPortalError || quotesError || invoicesError) {
    return null;
  }

  if (!clientPortalData) {
    return null;
  }

  const initialTab = quotesData?.length === 0 ? 'invoices' : 'quotes';

  return (
    <ClientPortal
      clientPortalData={clientPortalData}
      initialTab={initialTab}
      invoicesData={invoicesData || []}
      quotesData={quotesData || []}
    />
  );
}
